import { LoadingButton, Text, Title } from "@clipboard-health/ui-react";
import { isDefined } from "@clipboard-health/util-ts";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { API_TYPES } from "@src/appV2/api";
import { useState } from "react";

import { WORKER_CREDENTIAL_REQUEST_TYPE } from "./api/schema";
import { useChartingCredentialIntegrationConfig } from "./api/useChartingCredentialIntegrationConfig";
import { useCreateWorkerCredentialRequest } from "./api/useCreateWorkerCredentialRequest";

interface ResetCredentialsModalProps {
  onClose: () => void;
  workerName: string | undefined;
  onReset: () => void;
  workerId: string;
  workplaceId: string;
}

export function ResetCredentialsModal({
  onClose,
  workerName,
  onReset,
  workerId,
  workplaceId,
}: ResetCredentialsModalProps) {
  const { mutate: createWorkerCredentialRequest, isLoading: isCreatingWorkerCredentialRequest } =
    useCreateWorkerCredentialRequest();
  const [isResettingCredentials, setIsResettingCredentials] = useState(false);
  const chartingCredentialIntegrationConfig = useChartingCredentialIntegrationConfig();
  if (!chartingCredentialIntegrationConfig.enabled) {
    return null;
  }

  const { software } = chartingCredentialIntegrationConfig;
  const resetCredentialsFor = isDefined(workerName) ? ` for ${workerName}` : "";
  const title = `Reset ${software} account${resetCredentialsFor}?`;
  const text =
    "This will reset the account to your workplace's default one-time password, which will be sent to the professional.";
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={() => {
        onClose();
      }}
    >
      <DialogTitle>
        <Stack direction="column" spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Title component="h2" variant="h3">
              {title}
            </Title>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Text variant="body1">{text}</Text>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: (theme) => theme.palette.text.primary,
            paddingX: 4,
            paddingY: 1,
          }}
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          isLoading={isResettingCredentials || isCreatingWorkerCredentialRequest}
          variant="contained"
          sx={{
            paddingX: 4,
            paddingY: 1,
          }}
          onClick={async () => {
            setIsResettingCredentials(true);
            await new Promise<void>((resolve) => {
              setTimeout(() => {
                resolve();
              }, 1000);
            });
            createWorkerCredentialRequest({
              data: {
                type: WORKER_CREDENTIAL_REQUEST_TYPE,
                relationships: {
                  workplace: { data: { type: API_TYPES.workplace, id: workplaceId } },
                  worker: { data: { type: API_TYPES.worker, id: workerId } },
                },
                id: `${workplaceId}-${workerId}`,
              },
            });
            setIsResettingCredentials(false);
            onReset();
          }}
        >
          Reset
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
